// CDIO booth stress-test page. Public, no auth, scanned via QR. Calls the
// production AI pipeline (via /api/booth/coach) with no team/peer context
// -- the visitor types a comment + 6 PLAHER ratings, sees the AI coaching
// response.
//
// v8.1 (2026-06-02): replaced the 3 rule-engine strength bars (Substance /
// Structure / Tone) with two more honest signals:
//   - Live word count (deterministic, instant -- replaces the live bars)
//   - COIN checklist (4 indicators rendered from the AI inventory's coin{}
//     after Submit-for-AI-Review)
// The previous bars used pattern matching that misread crude register as
// balanced tone (Tom's "your work sucks" got Tone=100%) and undercounted
// COIN coverage on real student comments. The AI inventory is the
// authoritative signal; the rule engine remains as fallback only.
//
// Mobile-first: tested by inspection at 375px width.

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

const PLAHER_DIMENSIONS = [
  { code: 'P', label: 'Performance' },
  { code: 'L', label: 'Leadership' },
  { code: 'A', label: 'Attitude' },
  { code: 'H', label: 'Harmony' },
  { code: 'E', label: 'Expression' },
  { code: 'R', label: 'Reliability' },
];

const DEFAULT_LIKERT = {
  P: 3, L: 3, A: 3, H: 3, E: 3, R: 3,
};
const MAX_COMMENT_CHARS = 4000;
const WORD_COUNT_TARGET = 30; // soft target -- coaching is much more useful past this
const SESSION_STORAGE_KEY = 'itpBoothSessionId';

const wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;

// COIN checklist rendered from the AI inventory's coin{} field after
// Submit-for-AI-Review. Each element shows present (green check) or
// missing (gray circle). The AI is the authority on what's present;
// the inventory's coin{} is its assessment.
const COIN_ELEMENTS = [
  { key: 'context_present', label: 'Context', help: 'When or where it happened' },
  { key: 'observation_present', label: 'Observation', help: 'A specific action you saw' },
  { key: 'impact_present', label: 'Impact', help: 'What it meant for the team' },
  { key: 'next_steps_present', label: 'Next Steps', help: 'What to keep doing or change' },
];

function CoinChecklist({ coin }) {
  if (!coin) return null;
  return (
    <div className="border rounded p-2 mt-3 bg-white">
      <div className="small text-uppercase text-muted mb-2">
        COIN coverage (from AI assessment)
      </div>
      <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
        {COIN_ELEMENTS.map((el) => {
          const present = Boolean(coin[el.key]);
          return (
            <div
              key={el.key}
              className="d-flex align-items-center"
              style={{
                flex: '1 1 calc(50% - 8px)',
                minWidth: 0,
                padding: '6px 8px',
                background: present ? '#e8f5e9' : '#f5f5f5',
                border: `1px solid ${present ? '#a5d6a7' : '#e0e0e0'}`,
                borderRadius: '4px',
              }}
              title={el.help}
            >
              <span
                aria-label={present ? 'present' : 'missing'}
                style={{
                  display: 'inline-block',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: present ? '#2e7d32' : '#bdbdbd',
                  color: '#fff',
                  textAlign: 'center',
                  lineHeight: '18px',
                  fontSize: '12px',
                  fontWeight: 700,
                  marginRight: '8px',
                  flex: '0 0 18px',
                }}
              >
                {present ? '✓' : ''}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="small" style={{ fontWeight: 500 }}>{el.label}</div>
                <div className="small text-muted" style={{ fontSize: '0.75rem' }}>{el.help}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

CoinChecklist.propTypes = {
  coin: PropTypes.shape({
    context_present: PropTypes.bool,
    observation_present: PropTypes.bool,
    impact_present: PropTypes.bool,
    next_steps_present: PropTypes.bool,
  }),
};

CoinChecklist.defaultProps = {
  coin: null,
};

function LikertRow({ label, value, onChange }) {
  return (
    <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
      <span style={{ flex: '0 0 35%', minWidth: 0, fontSize: '0.95rem' }}>{label}</span>
      <div className="d-flex" style={{ gap: '6px', flex: 1 }} role="group" aria-label={`${label} rating`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={selected ? 'btn btn-primary btn-sm' : 'btn btn-outline-secondary btn-sm'}
              style={{ flex: 1, padding: '0.25rem 0', fontSize: '0.9rem' }}
              aria-pressed={selected}
              aria-label={`${label}: ${n} of 5`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

LikertRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function BoothPage() {
  const [sessionId, setSessionId] = useState(null);
  const [comment, setComment] = useState('');
  const [likert, setLikert] = useState(DEFAULT_LIKERT);
  const [response, setResponse] = useState(null); // { status: 'pending'|'done'|'error', ... }
  const [history, setHistory] = useState([]); // [{input, response}]
  const [error, setError] = useState(null);
  // v8.1: live rule-engine bars are gone. The live signal is now word
  // count (deterministic, no AI). The COIN checklist below renders from
  // the AI inventory after Submit-for-AI-Review.

  // On mount, obtain a session id. Persist in sessionStorage so refreshes
  // keep the same round counter (matches the QR-rescan-to-reset UX).
  useEffect(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (cached) {
      setSessionId(cached);
      return;
    }
    (async () => {
      try {
        const res = await fetch('/api/booth/session/new', { method: 'POST' });
        const data = await res.json();
        if (data && data.sessionId) {
          sessionStorage.setItem(SESSION_STORAGE_KEY, data.sessionId);
          setSessionId(data.sessionId);
        }
      } catch (e) {
        setError('Could not start a session. Please refresh the page.');
      }
    })();
  }, []);

  const submit = useCallback(async () => {
    if (!sessionId || !comment.trim() || response?.status === 'pending') return;
    setError(null);
    setResponse({ status: 'pending' });
    try {
      const res = await fetch('/api/booth/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, comment, likert }),
      });
      if (res.status === 429) {
        setResponse(null);
        setError('Slow down -- too many requests. Please wait a moment and try again.');
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setResponse(null);
        setError(data?.error || 'Something went wrong. Please try again.');
        return;
      }
      const done = { status: 'done', ...data };
      setResponse(done);
      setHistory((prev) => [
        { input: comment, likert, response: done },
        ...prev,
      ].slice(0, 10));
    } catch (e) {
      setResponse(null);
      setError('Network error. Please try again.');
    }
  }, [sessionId, comment, likert, response]);

  const tryAnother = useCallback(() => {
    setComment('');
    setResponse(null);
    setError(null);
    setLikert(DEFAULT_LIKERT);
  }, []);

  const setLikertDim = useCallback((code, val) => {
    setLikert((prev) => ({ ...prev, [code]: val }));
  }, []);

  const isPending = response?.status === 'pending';
  const isDone = response?.status === 'done';
  const isHostile = isDone && response.isHostile === true;
  const limitReached = isDone && (response.limitReached || response.dailyCapReached);
  const roundsRemaining = isDone && Number.isFinite(response.roundsRemaining)
    ? response.roundsRemaining : null;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '1rem' }}>
      <div className="mb-3">
        <h4 className="mb-1">Try the AI feedback coach</h4>
        <div className="small text-muted">
          Type a mock peer-feedback comment and see how the AI coach responds.
          No team context, no real data -- this is a demo. Try to surprise it.
        </div>
      </div>

      <div className="border rounded p-3 mb-3 bg-white">
        <div className="small text-uppercase text-muted mb-2">
          Your ratings for this teammate
        </div>
        {PLAHER_DIMENSIONS.map((d) => (
          <LikertRow
            key={d.code}
            label={d.label}
            value={likert[d.code]}
            onChange={(v) => setLikertDim(d.code, v)}
          />
        ))}
      </div>

      <Form.Group className="mb-2">
        <Form.Label className="small text-uppercase text-muted">
          Your written feedback
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={comment}
          maxLength={MAX_COMMENT_CHARS}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Write the feedback you would give your teammate..."
          disabled={isPending || limitReached}
        />
        {/* v8.1: live word count replaces the v8.0 char counter + strength bars.
            Shows progress toward a soft 30-word target -- the point at which
            feedback typically has room for context + observation + impact. */}
        <div className="small text-muted mt-1 d-flex" style={{ gap: '12px', flexWrap: 'wrap' }}>
          <span>
            {wordCount(comment)}
            {' words'}
            {wordCount(comment) >= WORD_COUNT_TARGET
              ? ' ✓' /* check */
              : ` (aim for ~${WORD_COUNT_TARGET}+ for useful feedback)`}
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {comment.length}
            {' / '}
            {MAX_COMMENT_CHARS}
            {' chars'}
          </span>
        </div>
      </Form.Group>

      <div className="d-flex flex-wrap mb-3" style={{ gap: '0.5rem' }}>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={submit}
          disabled={!sessionId || !comment.trim() || isPending || limitReached}
        >
          {isPending ? 'Generating coaching...' : 'Submit for AI Review'}
        </Button>
        {isDone && !limitReached && (
          <Button variant="primary" size="sm" onClick={tryAnother}>
            Try another
          </Button>
        )}
      </div>

      {error && (
        <div className="alert alert-warning py-2 small" role="alert">
          {error}
        </div>
      )}

      {isDone && (
        <div className="border rounded p-3 mt-2 bg-light">
          <div className="small text-uppercase text-muted mb-2">Coaching</div>
          {/* 2026-06-08: the "Tone flagged: HOSTILE" banner was REMOVED here too
              (matching the survey-flow removal) -- without context it confused
              users. isHostile is still computed and still gates the COIN
              checklist below + the hostile-redirect coaching path; only the
              user-facing red banner is gone. */}
          <div style={{ whiteSpace: 'pre-line' }}>
            {response.coaching || '(no coaching returned)'}
          </div>
          {response.fallbackUsed && (
            <div className="small text-muted mt-2">
              Showing rule-based coaching because AI coaching is unavailable.
            </div>
          )}
          {/* v8.1: COIN checklist driven by the AI inventory. Skipped on
              hostile (the hostile redirect is the priority) and ready
              (everything was already present anyway). */}
          {!isHostile && response.stage !== 'ready' && response.inventory && response.inventory.coin && (
            <CoinChecklist coin={response.inventory.coin} />
          )}
          {!limitReached && roundsRemaining !== null && (
            <div className="small text-muted mt-2">
              {roundsRemaining}
              {' '}
              tries remaining in this session.
            </div>
          )}
          {limitReached && (
            <div className="small text-muted mt-2">
              Rescan the QR code to start a fresh session.
            </div>
          )}
        </div>
      )}

      {history.length > 1 && (
        <details className="mt-4">
          <summary className="small text-muted" style={{ cursor: 'pointer' }}>
            Previous tries this session (
            {history.length - 1}
            )
          </summary>
          <div className="mt-2">
            {history.slice(1).map((h, i) => (
              <div
                key={`hist-${history.length - 1 - i}`}
                className="border rounded p-2 mb-2 bg-white small"
              >
                <div className="text-muted mb-1">Comment:</div>
                <div style={{ whiteSpace: 'pre-line' }} className="mb-2">
                  {h.input}
                </div>
                <div className="text-muted mb-1">Coaching:</div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {h.response.coaching}
                </div>
                {h.response.isHostile && (
                  <div className="badge bg-danger mt-1">Hostile</div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="text-center small text-muted mt-4">
        ITP Metrics &middot; 2026 conference demo &middot; not connected to a real survey
      </div>
    </div>
  );
}

export default BoothPage;
